import { OrderState, Direction, LockedQuote, Contract } from '@starx/shared-types';
import { RestClient } from '../api/rest_client';

export interface OrderForm {
  symbol: string;
  direction: Direction;
  durationSeconds: number;
  stake: string;
  currency: string;
}

export class OrderStore {
  private restClient: RestClient;
  private state: OrderState = 'IDLE';
  private currentQuote: LockedQuote | null = null;
  private activeContracts: Contract[] = [];
  private idempotencyKey: string = '';
  private quoteCountdownTimer: any = null;
  private remainingQuoteSeconds: number = 0;
  private listeners = new Set<() => void>();
  private errorMessage: string | null = null;
  private pendingStake: string | null = null;
  private pendingQuoteForm: OrderForm | null = null;
  private lastOrderForm: OrderForm | null = null;
  private challengeSiteKey: string | null = null;
  private quoteGeneration = 0;

  constructor(restClient: RestClient) {
    this.restClient = restClient;
  }

  public subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notify() {
    this.listeners.forEach(fn => {
      try {
        fn();
      } catch (err) {
        console.error('[OrderStore] Listener error:', err);
      }
    });
  }

  public getState(): OrderState {
    return this.state;
  }

  public getCurrentQuote(): LockedQuote | null {
    return this.currentQuote;
  }

  public getActiveContract(): Contract | null {
    return this.activeContracts.find(contract => contract.status === 'ACTIVE') || null;
  }

  public getActiveContracts(): Contract[] {
    return this.activeContracts.filter(contract => contract.status === 'ACTIVE');
  }

  public getRemainingQuoteSeconds(): number {
    return this.remainingQuoteSeconds;
  }

  public getErrorMessage(): string | null {
    return this.errorMessage;
  }

  public async requestQuote(form: OrderForm, turnstileToken?: string): Promise<LockedQuote | null> {
    if (this.state === 'REQUESTING_QUOTE' || this.state === 'SUBMITTING_ORDER') {
      return null;
    }

    const generation = this.quoteGeneration;
    this.lastOrderForm = { ...form };
    this.state = 'REQUESTING_QUOTE';
    this.errorMessage = null;
    this.notify();

    try {
      const quote = await this.restClient.requestQuote(
        form.symbol,
        form.direction,
        form.durationSeconds,
        form.currency,
        turnstileToken,
        form.stake,
      );

      if (generation !== this.quoteGeneration) return null;
      this.pendingQuoteForm = null;
      this.challengeSiteKey = null;
      this.currentQuote = quote;
      this.state = 'QUOTE_LOCKED';
      this.idempotencyKey = `IDEM-ORDER-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`;
      this.startQuoteTimer(10);
      this.notify();
      return quote;
    } catch (err: any) {
      if (generation !== this.quoteGeneration) return null;
      this.errorMessage = err.message || 'Quote request failed';
      if (err.code === 'FEED_DEGRADED') {
        this.state = 'FEED_DEGRADED';
      } else if (err.code === 'POLICY_VIOLATION') {
        this.state = 'POLICY_REJECTED';
      } else if (err.code === 'CHALLENGE_REQUIRED' || err.code === 'STEP_UP_CHALLENGE_REQUIRED') {
        this.pendingQuoteForm = form;
        this.challengeSiteKey = err.data?.site_key || null;
        this.state = 'CHALLENGE_REQUIRED';
      } else {
        this.state = 'IDLE';
      }
      this.notify();
      return null;
    }
  }

  public isQuoteExpired(): boolean {
    if (!this.currentQuote) return true;
    if (this.remainingQuoteSeconds <= 0) return true;
    const expiresAt = new Date(this.currentQuote.expires_at || 0).getTime();
    return !Number.isFinite(expiresAt) || expiresAt <= Date.now();
  }

  public getLastOrderForm(): OrderForm | null {
    return this.lastOrderForm;
  }

  public getPendingQuoteForm(): OrderForm | null {
    return this.pendingQuoteForm;
  }

  public getPendingStake(): string | null {
    return this.pendingStake;
  }

  private earlyCashoutQuotes = new Map<string, any>();

  public getEarlyCashoutQuote(contractId?: string): any {
    const quote = contractId
      ? this.earlyCashoutQuotes.get(contractId)
      : this.earlyCashoutQuotes.values().next().value;
    if (!quote) return null;
    const expiresAt = new Date(quote.expires_at || 0).getTime();
    if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
      this.earlyCashoutQuotes.delete(quote.contract_id || contractId || "");
      return null;
    }
    return quote;
  }

  public clearEarlyCashoutQuote(contractId: string) {
    this.earlyCashoutQuotes.delete(contractId);
  }

  public setActiveContract(contract: Contract) {
    const index = this.activeContracts.findIndex(item => item.contract_id === contract.contract_id);
    if (index >= 0) this.activeContracts[index] = contract;
    else this.activeContracts.push(contract);
    this.state = 'ACTIVE';
    this.notify();
  }

  public setOptimisticContract(contract: Contract) {
    const index = this.activeContracts.findIndex(item => item.contract_id === contract.contract_id);
    if (index >= 0) this.activeContracts[index] = contract;
    else this.activeContracts.push(contract);
    this.notify();
  }

  public replaceOptimisticContract(tempId: string, authoritativeContract: Contract) {
    const index = this.activeContracts.findIndex(item => item.contract_id === tempId);
    if (index >= 0) {
      this.activeContracts[index] = authoritativeContract;
    } else {
      this.activeContracts.push(authoritativeContract);
    }
    this.state = 'ACTIVE';
    this.notify();
  }

  public removeOptimisticContract(tempId: string) {
    const prevLen = this.activeContracts.length;
    this.activeContracts = this.activeContracts.filter(item => item.contract_id !== tempId);
    if (this.activeContracts.length !== prevLen) {
      this.notify();
    }
  }

  public getChallengeSiteKey(): string | null {
    return this.challengeSiteKey;
  }

  public async solveChallenge(turnstileToken: string): Promise<boolean> {
    if (this.pendingQuoteForm) {
      const form = this.pendingQuoteForm;
      this.pendingQuoteForm = null;
      // 1. Recover authoritative locked quote using challenge token
      const quote = await this.requestQuote(form, turnstileToken);
      if (!quote) return false;

      // 2. Resume original trading workflow to complete contract submission
      if (form.stake) {
        return await this.submitOrder(form.stake);
      }
      return true;
    }

    if (this.pendingStake) {
      const stake = this.pendingStake;
      // Handle quotes that expire while a submission-stage challenge is being solved
      if (this.isQuoteExpired()) {
        if (this.lastOrderForm) {
          // First attempt refreshing without a token to avoid reusing or burning turnstileToken
          let freshQuote = await this.requestQuote(this.lastOrderForm);
          if (!freshQuote) {
            // If the quote endpoint requires challenge verification, authenticate using turnstileToken
            if (this.state === 'CHALLENGE_REQUIRED' && turnstileToken) {
              freshQuote = await this.requestQuote(this.lastOrderForm, turnstileToken);
              if (freshQuote) {
                // turnstileToken has now been consumed by requestQuote and must NOT be reused for submitOrder
                this.pendingStake = stake;
                // Attempt submitOrder without reusing the single-use token; if challenged, submitOrder transitions cleanly to CHALLENGE_REQUIRED
                return await this.submitOrder(stake);
              }
            }
            return false;
          }
        } else {
          this.state = 'QUOTE_EXPIRED';
          this.errorMessage = 'Quote expired while completing verification challenge';
          this.notify();
          return false;
        }
      }
      return await this.submitOrder(stake, turnstileToken);
    }

    return false;
  }

  public cancelChallenge(): void {
    this.quoteGeneration++;
    this.challengeSiteKey = null;
    this.pendingStake = null;
    this.pendingQuoteForm = null;
    this.state = 'IDLE';
    this.notify();
  }

  public async submitOrder(
    stake: string,
    turnstileToken?: string,
    riskFree?: { is_risk_free?: boolean; risk_free_token_id?: string; risk_free_max_refund?: number; temp_optimistic_id?: string }
  ): Promise<boolean> {
    if ((this.state !== 'QUOTE_LOCKED' && this.state !== 'CHALLENGE_REQUIRED') || !this.currentQuote) {
      return false;
    }

    this.pendingStake = stake;
    this.state = 'SUBMITTING_ORDER';
    this.errorMessage = null;
    this.notify();

    const uniqueSuffix = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
    const contractId = `C-${this.currentQuote.symbol}-${uniqueSuffix}`;

    try {
      const res = await this.restClient.openContract(
        this.idempotencyKey,
        contractId,
        this.currentQuote.quote_id,
        stake,
        turnstileToken,
        riskFree,
      );

      clearInterval(this.quoteCountdownTimer);
      this.pendingStake = null;
      this.challengeSiteKey = null;
      this.state = 'ACCEPTED';

      const quote = this.currentQuote as any;
      const strikePrice = Number(quote?.strike_price ?? quote?.indicative_price ?? 0);
      const entryTimeMs = quote?.entry_timestamp ? new Date(quote.entry_timestamp).getTime() : Date.now();
      const durationSec = quote?.duration_seconds || 60;
      const expiryTimestamp = quote?.expiry_timestamp || new Date(entryTimeMs + durationSec * 1000).toISOString();
      const payoutPct = parseFloat(quote?.payout_percentage) || 90;
      const stakeNum = parseFloat(stake) || 0;
      const lockedPayout = quote?.locked_total_payout || (stakeNum * (1 + payoutPct / 100)).toFixed(2);

      // Instantly construct and activate contract from quote parameters with 0ms roundtrip delay!
      const authContract: Contract = {
        contract_id: contractId,
        user_id: this.currentQuote.user_id,
        quote_id: this.currentQuote.quote_id,
        symbol: this.currentQuote.symbol,
        direction: this.currentQuote.direction,
        currency: this.currentQuote.currency,
        stake: stake,
        payout_percentage: this.currentQuote.payout_percentage,
        locked_total_payout: lockedPayout,
        strike_price: strikePrice,
        strike_tick_id: quote?.strike_tick_id || 'TICK-ENTRY',
        strike_feed_source: quote?.strike_feed_source || 'OTC_FEED',
        entry_timestamp: new Date(entryTimeMs).toISOString(),
        expiry_timestamp: expiryTimestamp,
        status: 'ACTIVE',
        open_journal_tx_id: (res as any)?.tx_id || '',
        is_risk_free: riskFree?.is_risk_free || false,
        risk_free_token_id: riskFree?.risk_free_token_id || '',
        risk_free_max_refund: riskFree?.risk_free_max_refund || 0,
      };
      if (riskFree?.temp_optimistic_id) {
        this.activeContracts = this.activeContracts.filter(item => item.contract_id !== riskFree.temp_optimistic_id);
      }
      this.setActiveContractWithoutNotify(authContract);
      this.state = 'ACTIVE';
      this.notify();

      if (authContract && authContract.expiry_timestamp) {
        const delayMs = Math.max(100, new Date(authContract.expiry_timestamp).getTime() - Date.now() + 500);
        setTimeout(async () => {
          if (this.activeContracts.some(item => item.contract_id === contractId && item.status === 'ACTIVE')) {
            try {
              const settled = await this.restClient.getContract(contractId);
              if (settled && settled.status !== 'ACTIVE') {
                this.handleSettlement(settled);
              }
            } catch {}
          }
        }, delayMs);
      }

      // Non-blocking background sync with authoritative backend record
      void this.restClient.getContract(contractId).then(fresh => {
        if (fresh && fresh.contract_id === contractId) {
          this.setActiveContractWithoutNotify(fresh);
          this.notify();
        }
      }).catch(() => {});

      return true;
    } catch (err: any) {
      if ((err.code === 'RATE_LIMIT_EXCEEDED' || err.code === 'TOO_MANY_REQUESTS' || String(err.message).includes('velocity')) && !(this as any)._submittingRetry) {
        (this as any)._submittingRetry = true;
        await new Promise(res => setTimeout(res, 250));
        const retryResult = await this.submitOrder(stake, turnstileToken);
        (this as any)._submittingRetry = false;
        return retryResult;
      }
      this.errorMessage = err.message || 'Contract submission failed';
      if (err.code === 'INSUFFICIENT_BALANCE') {
        this.state = 'INSUFFICIENT_BALANCE';
      } else if (err.code === 'QUOTE_EXPIRED' || err.code === 'EXPIRED_QUOTE') {
        this.state = 'QUOTE_EXPIRED';
        this.errorMessage = 'Authoritative quote expired before order placement';
      } else if (err.code === 'CHALLENGE_REQUIRED' || err.code === 'STEP_UP_CHALLENGE_REQUIRED') {
        this.state = 'CHALLENGE_REQUIRED';
        this.challengeSiteKey = err.data?.site_key || '1x00000000000000000000AA';
      } else {
        this.state = 'IDLE';
      }
      this.notify();
      return false;
    }
  }

  public async reconcileActive(): Promise<Contract[]> {
    try {
      const serverContracts: Contract[] = await this.restClient.getActiveContracts().catch(() => []);
      const now = Date.now();

      // Build updated active contracts list:
      // 1. Never discard unexpired contracts (grace period: 2 seconds)
      // 2. Always preserve active DEMO contracts until local demo settlement fires
      const contractMap = new Map<string, Contract>();

      // Keep existing unexpired, demo, or optimistic contracts
      for (const existing of this.activeContracts) {
        const isDemo = existing.contract_id?.startsWith('DEMO-');
        const isOptimistic = existing.contract_id?.startsWith('opt-');
        const expiryMs = existing.expiry_timestamp ? new Date(existing.expiry_timestamp).getTime() : 0;
        const isUnexpired = expiryMs > (now - 2000);
        if (isOptimistic || (existing.status === 'ACTIVE' && (isDemo || isUnexpired))) {
          contractMap.set(existing.contract_id, existing);
        }
      }

      // Merge server-confirmed active contracts
      if (Array.isArray(serverContracts)) {
        for (const sc of serverContracts) {
          if (sc && sc.contract_id) {
            contractMap.set(sc.contract_id, sc);
          }
        }
      }

      // Preserve any optimistic contracts added to this.activeContracts while getActiveContracts was in-flight
      for (const existing of this.activeContracts) {
        if (existing.contract_id?.startsWith('opt-')) {
          contractMap.set(existing.contract_id, existing);
        }
      }

      this.activeContracts = Array.from(contractMap.values());
      if (this.activeContracts.length > 0) {
        this.state = 'ACTIVE';
      } else if (this.state === 'ACTIVE' || this.state === 'ACCEPTED') {
        this.state = 'IDLE';
      }

      this.notify();
      return this.getActiveContracts();
    } catch (err: any) {
      console.warn('Failed to reconcile active contract:', err);
      return this.getActiveContracts();
    }
  }

  public async requestEarlyCashout(contractId: string): Promise<any> {
    try {
      const activeContract = this.activeContracts.find(item => item.contract_id === contractId);
      if (contractId.startsWith('DEMO-') && activeContract) {
        const quote = {
          quote_id: `DEMO-CASHOUT-QUOTE-${Date.now()}`,
          contract_id: contractId,
          user_id: activeContract.user_id,
          quote_payout: (parseFloat(activeContract.stake) * 0.65).toFixed(2),
          indicative_price: activeContract.strike_price,
          currency: activeContract.currency,
          expires_at: new Date(Date.now() + 5000).toISOString(),
          is_consumed: false,
        };
        this.earlyCashoutQuotes.set(contractId, quote);
        this.notify();
        return quote;
      }
      const quote = await this.restClient.quoteEarlyCashout(contractId);
      this.earlyCashoutQuotes.set(contractId, quote);
      this.notify();
      return quote;
    } catch (err: any) {
      // Background cashout evaluation failure (e.g. CASHOUT_POSITION_NOT_ADVERSE, CASHOUT_WINDOW_CLOSED, STALE_FEED)
      // is normal market state and must NEVER pollute order placement errorMessage or show red alerts to the trader.
      this.earlyCashoutQuotes.delete(contractId);
      return null;
    }
  }

  public async executeEarlyCashout(contractId?: string): Promise<boolean> {
    const quote = contractId ? this.earlyCashoutQuotes.get(contractId) : this.getEarlyCashoutQuote();
    if (!quote) return false;
    try {
      const activeContract = this.activeContracts.find(item => item.contract_id === quote.contract_id);
      if (quote.contract_id.startsWith('DEMO-') && activeContract) {
        activeContract.status = 'SETTLED_CASHOUT';
        activeContract.actual_payout = quote.quote_payout;
        activeContract.settled_at = new Date().toISOString();
        activeContract.settlement_timestamp = new Date().toISOString();
        activeContract.settlement_evidence_hash = `DEMO_CASHOUT_EVIDENCE_${activeContract.contract_id}`;
        this.activeContracts = this.activeContracts.filter(item => item.contract_id !== quote.contract_id);
        this.state = this.activeContracts.length ? 'ACTIVE' : 'SETTLED';
        this.earlyCashoutQuotes.delete(quote.contract_id);
        this.notify();
        return true;
      }
      const idempKey = `IDEMP-CASHOUT-${quote.quote_id}`;
      const res = await this.restClient.executeEarlyCashout(quote.quote_id, idempKey);
      if (activeContract) {
        activeContract.status = 'SETTLED_CASHOUT';
        activeContract.actual_payout = String(res.cashout_payout ?? res.payout ?? quote.quote_payout);
        activeContract.settled_at = new Date().toISOString();
      }
      this.activeContracts = this.activeContracts.filter(item => item.contract_id !== quote.contract_id);
      this.state = this.activeContracts.length ? 'ACTIVE' : 'SETTLED';
      this.earlyCashoutQuotes.delete(quote.contract_id);
      this.notify();
      return true;
    } catch (err: any) {
      this.errorMessage = err.message || 'Early cashout execution failed';
      this.notify();
      return false;
    }
  }

  private settlementCallbacks: Array<(contract: Contract) => void> = [];

  public onSettlement(cb: (contract: Contract) => void) {
    this.settlementCallbacks.push(cb);
  }

  public handleSettlement(contract: Contract) {
    this.activeContracts = this.activeContracts.filter(item => item.contract_id !== contract.contract_id);
    this.state = this.activeContracts.length ? 'ACTIVE' : 'SETTLED';
    this.earlyCashoutQuotes.delete(contract.contract_id);
    this.notify();
    this.settlementCallbacks.forEach(cb => {
      try { cb(contract); } catch {}
    });
  }

  public reset() {
    this.quoteGeneration++;
    clearInterval(this.quoteCountdownTimer);
    this.state = 'IDLE';
    this.currentQuote = null;
    this.activeContracts = [];
    this.earlyCashoutQuotes.clear();
    this.errorMessage = null;
    this.notify();
  }

  public resetQuote() {
    this.quoteGeneration++;
    this.pendingStake = null;
    this.pendingQuoteForm = null;
    this.lastOrderForm = null;
    this.challengeSiteKey = null;
    this.remainingQuoteSeconds = 0;
    clearInterval(this.quoteCountdownTimer);
    this.currentQuote = null;
    this.errorMessage = null;
    this.state = this.activeContracts.length ? 'ACTIVE' : 'IDLE';
    this.notify();
  }

  private setActiveContractWithoutNotify(contract: Contract) {
    const index = this.activeContracts.findIndex(item => item.contract_id === contract.contract_id);
    if (index >= 0) this.activeContracts[index] = contract;
    else this.activeContracts.push(contract);
  }

  private startQuoteTimer(seconds: number) {
    clearInterval(this.quoteCountdownTimer);
    this.remainingQuoteSeconds = seconds;
    this.quoteCountdownTimer = setInterval(() => {
      this.remainingQuoteSeconds--;
      if (this.remainingQuoteSeconds <= 0) {
        clearInterval(this.quoteCountdownTimer);
        if (this.state === 'QUOTE_LOCKED') {
          this.state = 'QUOTE_EXPIRED';
          this.currentQuote = null;
          this.notify();
        }
      } else {
        this.notify();
      }
    }, 1000);
  }
}
